import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { ArrowRight, Send } from 'lucide-react';
import { toast } from 'sonner';

// --- ÖNEMLİ DÜZELTME: API ADRESİNİ SABİTLİYORUZ ---
// Telefondan girince hata vermemesi için direkt canlı adresi yazıyoruz.
const API = "https://mertt.tr/api";

// --- RESİM URL DÜZELTİCİ ---
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url; 
  
  // Resim yolunun başında / var mı yok mu kontrol et
  const cleanPath = url.startsWith('/') ? url : `/${url}`;
  
  // Sonuç: https://mertt.tr/uploads/resim.jpg
  return `https://mertt.tr${cleanPath}`;
};

function HomePage() {
  const { t, language } = useLanguage();
  const [services, setServices] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // API'yi sabit adresten çekiyoruz
      const [servicesRes, blogsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/blogs?published=true`),
        axios.get(`${API}/settings`)
      ]);
      
      // Veri güvenliği (Liste kontrolü)
      const servicesData = servicesRes.data;
      if (Array.isArray(servicesData)) {
          setServices(servicesData);
      } else if (servicesData && Array.isArray(servicesData.data)) {
          setServices(servicesData.data);
      } else {
          setServices([]);
      }

      setBlogs(blogsRes.data.slice(0, 3));
      setSettings(settingsRes.data || {});
    } catch (error) {
      console.error('Veri çekme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success(t('contact.success'));
      setContactForm({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error(t('contact.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Seo />
      <Navbar />

      {/* --- HERO SECTION --- */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Arka Plan Resmi */}
        <div className="absolute inset-0 z-0">
            <img 
                src="https://images.unsplash.com/photo-1485740112426-0c2549fa8c86?q=80&w=2074&auto=format&fit=crop" 
                alt="Hero Background" 
                className="w-full h-full object-cover opacity-50"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h1 className="font-emblema text-4xl sm:text-5xl lg:text-7xl mb-6 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600 animate-fadeIn">
            {t('hero.title')}
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-8 max-w-2xl mx-auto animate-fadeIn" style={{ animationDelay: '0.2s' }}>
            {t('hero.subtitle')}
          </p>
          <a
            href="#services"
            className="inline-flex items-center space-x-2 bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-3 rounded-full font-bold transition transform hover:scale-105 animate-fadeIn"
            style={{ animationDelay: '0.4s' }}
          >
            <span>{t('hero.cta')}</span>
            <ArrowRight size={20} />
          </a>
        </div>

        {/* Scroll İkonu */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-cyan-400 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-cyan-400 rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* --- SERVICES SECTION --- */}
      <section id="services" className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-emblema text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 mb-4">
              {t('services.title')}
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
               <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
            </div>
          ) : (
            <>
              {/* Hizmet Kartları (İlk 4 tanesi) */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {services.slice(0, 4).map((service, index) => (
                  <Link
                    key={service.id}
                    to={`/services/${service.id}`}
                    className="group bg-gray-800 rounded-xl overflow-hidden hover:shadow-2xl hover:shadow-cyan-500/20 transition duration-300 border border-gray-700 flex flex-col h-full"
                  >
                    <div className="h-48 overflow-hidden relative">
                      <img
                        src={getImageUrl(service.image_url)}
                        alt={language === 'tr' ? service.title_tr : service.title_en}
                        className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                        onError={(e) => {e.target.src = 'https://via.placeholder.com/300?text=Hizmet'}}
                      />
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition"></div>
                    </div>
                    
                    <div className="p-6 flex flex-col flex-grow">
                        <h3 className="text-xl font-bold text-cyan-400 mb-2 group-hover:text-white transition">
                        {language === 'tr' ? service.title_tr : service.title_en}
                        </h3>
                        <p className="text-gray-400 text-sm line-clamp-3 mb-4 flex-grow">
                        {language === 'tr' ? service.description_tr : service.description_en}
                        </p>
                        <div className="flex items-center text-cyan-400 text-sm font-semibold group-hover:translate-x-2 transition-transform mt-auto">
                        <span>{t('blog.readMore')}</span>
                        <ArrowRight size={16} className="ml-2" />
                        </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Tüm Hizmetler Butonu */}
              <div className="text-center mt-12">
                <Link 
                  to="/services" 
                  className="inline-flex items-center space-x-3 bg-transparent border border-cyan-500 text-cyan-400 hover:bg-cyan-600 hover:text-white px-8 py-3 rounded-full font-bold transition-all duration-300 transform hover:scale-105"
                >
                  <span>Tüm Hizmetleri İncele</span>
                  <ArrowRight size={20} />
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* --- ABOUT SECTION (RESİM DÜZELTİLDİ) --- */}
      <section className="py-20 relative overflow-hidden">
         {/* Arka Plan Resmi - YENİ VE SAĞLAM LİNK */}
         <div className="absolute inset-0 z-0">
             <img 
               src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=2070&auto=format&fit=crop" 
               alt="About Background" 
               className="w-full h-full object-cover opacity-30"
             />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black"></div>
         </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl sm:text-5xl font-emblema text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-600 mb-6">
              {settings.about_title || t('about.title')}
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed whitespace-pre-line mb-8">
              {settings.about_desc || t('about.content')}
            </p>
            <Link 
              to="/about" 
              className="inline-flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-full font-bold transition transform hover:scale-105"
            >
              <span>Şirket Hakkında Detaylı Bilgi</span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* --- LATEST BLOG POSTS --- */}
      {blogs.length > 0 && (
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-emblema text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-500 mb-4">
                {t('blog.latest')}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogs.map((blog) => (
                <Link
                  key={blog.id}
                  to={`/blog/${blog.id}`}
                  className="group bg-black rounded-xl overflow-hidden border border-gray-800 hover:border-green-500/50 transition duration-300"
                >
                  <div className="h-48 overflow-hidden relative">
                    <img
                      src={getImageUrl(blog.image_url)}
                      alt={language === 'tr' ? blog.title_tr : blog.title_en}
                      className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                      onError={(e) => {e.target.src = 'https://via.placeholder.com/300?text=Blog'}}
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-green-400 mb-2 group-hover:text-white transition">
                      {language === 'tr' ? blog.title_tr : blog.title_en}
                    </h3>
                    <p className="text-gray-400 text-sm line-clamp-2 mb-4">
                      {language === 'tr' ? blog.excerpt_tr : blog.excerpt_en}
                    </p>
                    <div className="flex items-center text-green-400 text-sm font-medium group-hover:translate-x-2 transition-transform">
                      <span>{t('blog.readMore')}</span>
                      <ArrowRight size={16} className="ml-2" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link to="/blog" className="inline-block border border-gray-600 text-gray-300 hover:text-white hover:border-white px-8 py-3 rounded-full transition">
                {t('blog.title')}
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* --- CONTACT SECTION --- */}
      <section id="contact" className="py-20 relative">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl sm:text-5xl font-emblema text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500 mb-4">
                {t('contact.title')}
              </h2>
            </div>

            <form onSubmit={handleContactSubmit} className="bg-gray-900/80 backdrop-blur-sm p-8 rounded-2xl border border-gray-800 space-y-6 shadow-2xl">
              <div>
                <input
                  type="text"
                  placeholder={t('contact.name')}
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="w-full bg-black border border-gray-700 rounded-lg p-4 text-white focus:border-cyan-500 outline-none transition"
                  required
                />
              </div>
              <div>
                <input
                  type="email"
                  placeholder={t('contact.email')}
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="w-full bg-black border border-gray-700 rounded-lg p-4 text-white focus:border-cyan-500 outline-none transition"
                  required
                />
              </div>
              <div>
                <input
                  type="text"
                  placeholder={t('contact.subject')}
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                  className="w-full bg-black border border-gray-700 rounded-lg p-4 text-white focus:border-cyan-500 outline-none transition"
                  required
                />
              </div>
              <div>
                <textarea
                  placeholder={t('contact.message')}
                  rows="5"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  className="w-full bg-black border border-gray-700 rounded-lg p-4 text-white focus:border-cyan-500 outline-none transition"
                  required
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-bold py-4 rounded-lg transition transform active:scale-95 flex items-center justify-center space-x-2"
              >
                {submitting ? (
                   <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                ) : (
                  <>
                    <span>{t('contact.send')}</span>
                    <Send size={20} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default HomePage;